import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

let _bearer = null;

export function setBearer(token) {
  _bearer = token;
  // localStorage can throw on iOS Safari with "Block All Cookies" / Private
  // Browsing. Persisting the token is best-effort — the in-memory _bearer keeps
  // the current session working even when Web Storage is unavailable, so a
  // successful login is never reported as a failure.
  try {
    if (token) {
      localStorage.setItem("st_access_token", token);
    } else {
      localStorage.removeItem("st_access_token");
    }
  } catch { /* storage blocked — rely on in-memory bearer + cookie */ }
}

export function bootstrapBearer() {
  try {
    const t = localStorage.getItem("st_access_token");
    if (t) _bearer = t;
  } catch { /* storage blocked */ }
}

api.interceptors.request.use((config) => {
  if (_bearer) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${_bearer}`;
  }
  return config;
});

let _refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    const status = err?.response?.status;
    const url = original?.url || "";
    const isAuthPath = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"].some((p) => url.includes(p));
    if (status === 401 && original && !original._retried && !isAuthPath) {
      original._retried = true;
      try {
        // Single-flight: concurrent 401s all await the same refresh call.
        if (!_refreshing) {
          _refreshing = api.post("/auth/refresh").finally(() => { _refreshing = null; });
        }
        const { data } = await _refreshing;
        if (data?.access_token) {
          setBearer(data.access_token);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${data.access_token}`;
        }
        return api(original);
      } catch {
        setBearer(null);
        return Promise.reject(err);
      }
    }
    if (status === 401 && url.includes("/auth/refresh")) setBearer(null);
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function downloadFile(url, filename) {
  // Use fetch+blob so we can stream the binary with credentials
  return fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: _bearer ? { Authorization: `Bearer ${_bearer}` } : {},
  })
    .then((r) => {
      if (!r.ok) throw new Error("Download failed");
      return r.blob();
    })
    .then((blob) => {
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    });
}
